import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import { motion } from "framer-motion";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import face1 from "../../static/media/FacialStetic/face1.webp";
import face2 from "../../static/media/FacialStetic/face2.webp";
import face3 from "../../static/media/FacialStetic/face3.webp";
import face4 from "../../static/media/FacialStetic/face4.webp";
import face5 from "../../static/media/FacialStetic/face5.webp";
import face6 from "../../static/media/FacialStetic/face6.webp";

const PRIMARY = "#06BBC7";

const services = [
  {
    title: "Limpieza profunda facial",
    description:
      "Renueva tu piel eliminando impurezas, exceso de grasa y células muertas para lograr un rostro más fresco y limpio.",
    image: face1,
  },
  {
    title: "Tratamiento antiage",
    description:
      "Cuidado facial enfocado en mejorar la apariencia de líneas de expresión, firmeza y luminosidad de la piel.",
    image: face2,
  },
  {
    title: "Tratamiento acné",
    description:
      "Protocolos profesionales para pieles con tendencia acneica, ayudando a mejorar textura, limpieza y equilibrio.",
    image: face3,
  },
  {
    title: "Tratamiento despigmentante",
    description:
      "Ayuda a unificar el tono de la piel y mejorar la apariencia de manchas con técnicas estéticas especializadas.",
    image: face4,
  },
  {
    title: "Hidratación profunda",
    description:
      "Aporta frescura, suavidad y luminosidad mediante tratamientos que revitalizan la piel desde la primera sesión.",
    image: face5,
  },
  {
    title: "Aparatología facial",
    description:
      "Tecnología estética aplicada al cuidado facial para potenciar resultados de forma segura y profesional.",
    image: face6,
  },
];

const CardMotion = motion(Box);

const Intro = () => {
  const whatsappMessage = encodeURIComponent(
    "Hola Lily's Sunflower, quiero saber más sobre estética facial."
  );

  const whatsappUrl = `https://wa.me/593984781610?text=${whatsappMessage}`;

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 5, md: 9 },
        background:
          "linear-gradient(180deg, #ffffff 0%, #ffffff 70%, rgba(6, 187, 199, 0.08) 100%)",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ textAlign: "center", mb: { xs: 3.5, md: 6 } }}>
          <Box
            sx={{
              width: { xs: 180, sm: 280 },
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.4,
              color: PRIMARY,
            }}
          >
            <Box sx={{ flex: 1, height: 2, bgcolor: PRIMARY, borderRadius: 10 }} />
            <Box sx={{ width: 7, height: 7, bgcolor: PRIMARY, borderRadius: "50%" }} />
            <Typography
              component="span"
              sx={{
                fontSize: { xs: 25, sm: 32 },
                lineHeight: 1,
                color: PRIMARY,
                fontWeight: 300,
              }}
            >
              ✽
            </Typography>
            <Box sx={{ width: 7, height: 7, bgcolor: PRIMARY, borderRadius: "50%" }} />
            <Box sx={{ flex: 1, height: 2, bgcolor: PRIMARY, borderRadius: 10 }} />
          </Box>

          <Typography
            component="h2"
            sx={{
              fontFamily: "'Playfair Display', serif",
              fontSize: { xs: "2rem", sm: "2.7rem", md: "3.5rem" },
              fontWeight: 500,
              color: "#1d2530",
              letterSpacing: "-0.04em",
              mb: 2,
            }}
          >
            Estética Facial
          </Typography>

          <Typography
            sx={{
              maxWidth: 760,
              mx: "auto",
              color: "#374151",
              fontSize: { xs: "0.95rem", md: "1.1rem" },
              lineHeight: 1.75,
              fontWeight: 400,
              px: { xs: 1, sm: 0 },
            }}
          >
            Cuida tu piel con tratamientos diseñados para limpiar, hidratar,
            renovar y realzar la belleza natural de tu rostro.
          </Typography>
        </Box>

        <Box
          sx={{
            maxWidth: 1180,
            mx: "auto",
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
            },
            gap: { xs: 1.4, sm: 2, md: 2.4 },
          }}
        >
          {services.map((service, index) => (
            <CardMotion
              key={service.title}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.18 }}
              transition={{
                duration: 0.45,
                delay: index * 0.055,
                ease: "easeOut",
              }}
              sx={{
                bgcolor: "#ffffff",
                borderRadius: { xs: "16px", md: "22px" },
                overflow: "hidden",
                border: "1px solid rgba(6, 187, 199, 0.13)",
                boxShadow: {
                  xs: "0 10px 24px rgba(15, 23, 42, 0.07)",
                  md: "0 16px 38px rgba(15, 23, 42, 0.08)",
                },
                transition: "box-shadow 0.25s ease, border-color 0.25s ease",
                "&:hover": {
                  boxShadow: "0 22px 52px rgba(15, 23, 42, 0.14)",
                  borderColor: "rgba(6, 187, 199, 0.35)",
                },
              }}
            >
              <Box
                sx={{
                  height: { xs: 125, sm: 180, md: 230 },
                  bgcolor: "#f8fbfc",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={service.image}
                  alt={service.title}
                  loading="lazy"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                    display: "block",
                  }}
                />
              </Box>

              <Box
                sx={{
                  p: { xs: 1.35, sm: 2, md: 2.4 },
                  textAlign: "center",
                }}
              >
                <Typography
                  component="h3"
                  sx={{
                    color: "#1f2937",
                    fontSize: { xs: "0.83rem", sm: "1.05rem", md: "1.16rem" },
                    fontWeight: 800,
                    lineHeight: 1.18,
                    mb: { xs: 0, sm: 1 },
                  }}
                >
                  {service.title}
                </Typography>

                <Box
                  sx={{
                    width: { xs: 34, sm: 42 },
                    height: 2,
                    bgcolor: PRIMARY,
                    borderRadius: 10,
                    mx: "auto",
                    my: { xs: 1, sm: 1.2 },
                  }}
                />

                <Typography
                  sx={{
                    display: { xs: "none", sm: "block" },
                    color: "#4b5563",
                    fontSize: { sm: "0.88rem", md: "0.95rem" },
                    lineHeight: 1.6,
                    fontWeight: 400,
                  }}
                >
                  {service.description}
                </Typography>
              </Box>
            </CardMotion>
          ))}
        </Box>

        <Box
          sx={{
            mt: { xs: 4.5, md: 6 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Typography
            sx={{
              color: "#1f2937",
              fontSize: { xs: "0.98rem", md: "1.1rem" },
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            Dale a tu rostro el cuidado que merece.
          </Typography>

          <Button
            component="a"
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            endIcon={<WhatsAppIcon />}
            sx={{
              bgcolor: PRIMARY,
              color: "#ffffff",
              px: { xs: 3.2, md: 4 },
              py: 1.2,
              borderRadius: "999px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: { xs: "0.95rem", md: "1rem" },
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#05aeb9",
                boxShadow: "none",
              },
            }}
          >
            Quiero saber más
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Intro;